import clsx from "classnames";
import React from "react";

import styles from "./index.module.css";

// ===================================================
// Text
// ===================================================

interface TextProps {
  /** Type of HTML element, defaults to `p` */
  as?: React.ElementType;
  /** Text to render.*/
  children: React.ReactNode;
  /** Size of Text */
  size?: "lg" | "base" | "sm" | "x-sm" | "xx-sm";
  /** Theme of Text */
  theme?: "base" | "muted" | "inverse";
  /** Font weight of text */
  weight?: "regular" | "medium" | "bold";
  /** Font family of text */
  family?: "sans" | "mono";
  /** Alignment of text */
  align?: "left" | "center" | "right";
  /** Override classes */
  className?: string;
}

/**
 * Used to output text, like paragraphs and spans.
 */

const Text: React.FC<TextProps> = ({
  as: As = "p",
  weight = "regular",
  size = "base",
  theme = "base",
  family = "sans",
  align = "left",
  className,
  ...rest
}) => {
  const classes = clsx(
    styles.base,
    {
      [styles[`text-size-${size}`]]: size,
      [styles[weight]]: weight,
      [styles[theme]]: theme,
      [styles[family]]: family,
      [styles[align]]: align,
    },
    className
  );

  return <As className={classes} {...rest} />;
};

// ===================================================
// Heading
// ===================================================

// Define a type alias for allowed heading elements or div
export type headingEl = "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

interface HeadingProps {
  /** Type of heading level to output, defaults to a `div` */
  as?: headingEl;
  /** Heading to render.*/
  children: React.ReactNode;
  /** Size of Heading */
  size?: "base" | "sm" | "x-sm";
  /** Override classes */
  className?: string;
}

/**
 * Used to output large, bold headings.
 */

const Heading: React.FC<HeadingProps> = ({
  as = "div",
  size = "base",
  className,
  ...rest
}) => {
  const classes = clsx(
    styles.base,
    {
      [styles.sans]: true,
      [styles[`heading-size-${size}`]]: size,
    },
    className
  );

  const As: headingEl = as;

  return <As className={classes} {...rest} />;
};

export { Heading, Text };
